// @flow
import {useState} from 'react';
import type {Node} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {Box} from '@wellstone-solutions/web';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {membersQueryOptions} from 'queries/members';
import {useMembersCounts} from '../../queries/useMembersCounts';
import {
  RowCount,
  PaginatedListGrid,
  usePaginationParams,
} from 'modules/datagrid';
import {useStores} from 'hooks/useStores';
import {memberListColumns} from './columns';
import {ResendInvitationModal} from './ResendInvitationModal';
import {UnassignedEmptyState} from 'components/UnassignedEmptyState';

const ROLE_PATIENT = ['patient'];

export const UnassignedMembersTab = (): Node => {
  const {meStore} = useStores();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [memberToInvite, setMemberToInvite] = useState(null);

  const {page, setPage, pageSize, setPageSize, ordering, setOrdering} =
    usePaginationParams({
      defaultSortModel: [{field: 'member__name', sort: 'asc'}],
    });

  const countsQuery = useMembersCounts({role: ROLE_PATIENT});
  const unassignedCount = countsQuery.data?.unassigned ?? 0;

  const query = useQuery(
    membersQueryOptions.list({
      unassigned: true,
      role: ROLE_PATIENT,
      page,
      pageSize,
      ordering,
    }),
  );

  const rows =
    query.data?.results?.map((m) => ({
      id: m.id,
      member: m,
    })) ?? [];

  const columns = isMobile
    ? [memberListColumns.mobile]
    : [
        memberListColumns.name,
        memberListColumns.lastAppOpen,
        memberListColumns.dateProfileCreated,
      ];

  if (meStore.ehrIntegrationOption && !isMobile) {
    columns.push({
      ...memberListColumns.ehr,
      headerName:
        meStore.ehrIntegrationOption?.name ?? memberListColumns.ehr.headerName,
    });
  }

  columns.push(
    memberListColumns.getRowActions({onResendInvite: setMemberToInvite}),
  );

  const handleRowClick = ({row}) => {
    if (globalThis.getSelection()?.type === 'Range') {
      return;
    }
    navigate(`/members/${row.member.id}`);
  };

  if (unassignedCount === 0) {
    return <UnassignedEmptyState />;
  }

  return (
    <Box>
      <RowCount
        isLoading={query.isFetching}
        label="unassigned member"
        count={query.data?.count || 0}
      />

      <PaginatedListGrid
        testId="unassigned-members-datagrid"
        columns={columns}
        rows={rows}
        rowCount={query.data?.count || 0}
        isLoading={query.isFetching}
        page={page}
        pageSize={pageSize}
        ordering={ordering}
        isMobile={isMobile}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortModelChange={setOrdering}
        onRowClick={handleRowClick}
      />

      <ResendInvitationModal
        member={memberToInvite}
        open={!!memberToInvite}
        onComplete={() => {
          setMemberToInvite(null);
        }}
      />
    </Box>
  );
};
